import { ShieldCheck, AlertTriangle, Lightbulb } from 'lucide-react';

export const epiArticle = {
  slug: 'equipement-complet-motard-2026',
  weekVolume: 27,
  weekYear: 2026,
  monthLabel: 'Juillet 2026',
  category: 'Équipement',
  shortTitle: "L'équipement complet",
  title: "L'équipement complet du motard",
  kicker: 'EPI 2026 — obligatoires & recommandés',
  subtitle: "Casque, gants, blouson, pantalon, bottes, dorsale, airbag… le panorama complet des équipements de protection individuelle (EPI) du motard en 2026.",
  heroImage: 'https://images.unsplash.com/photo-1544148024-c3759a58ba99?w=2400&q=80&auto=format&fit=crop',
  heroImageAlt: 'Motard en équipement complet de protection',
  topRightKicker: { line1: 'Conseil ECSR', line2: 'Spécial EPI' },
  readTimeMin: 5,
  seoDescription: "Tous les EPI motard 2026 : casque ECE 22-06, gants EN 13594, blouson EN 17092, dorsale EN 1621-2, bottes EN 13634, airbag.",
  seoKeywords: "EPI motard, équipement moto obligatoire, EN 17092, EN 13594, EN 1621-2, dorsale moto, airbag moto",
  source: { label: 'Sécurité routière + INRS', refs: 'Articles R431-1 et R431-1-2 du Code de la route, normes européennes EN harmonisées.' },
  chapo: {
    kicker: 'L’adage motard',
    body: "« On s’habille pour la chute, pas pour la balade. » En 2026, seuls le <strong>casque</strong> (ECE 22-06) et les <strong>gants</strong> (EN 13594) sont légalement obligatoires. Mais l’équipement complet sauve des vies.",
    sideStat: { kicker: 'Statistique', big: '70 %', body: "des motards ont chuté au moins une fois. Statistique ECSR — d’où l’importance d’un équipement complet.", accent: '#cf2031' },
  },
  pullQuote: { text: "Une glissade à 50 km/h sur du bitume sans pantalon protecteur provoque des brûlures de l’épiderme profondes (parfois greffes nécessaires) et des fractures de hanche/genou fréquentes.", source: 'IRTAD — Études accidentologiques' },
  sections: [
    {
      part: 'Partie 1 — Obligatoires',
      title: 'Les 2 EPI imposés par la loi',
      cards: [
        { icon: ShieldCheck, accent: '#cf2031', kicker: 'Obligation 1', title: 'Casque homologué ECE 22-06', body: "Article R431-1 : pour conducteur ET passager. Sanction : <strong>135 € + 3 points</strong>. <a href='/magazine/bien-choisir-casque-moto' class='underline'>Lire le guide casque →</a>" },
        { icon: ShieldCheck, accent: '#cf2031', kicker: 'Obligation 2', title: 'Gants homologués EN 13594', body: "Article R431-1-2 (depuis 2016). Marquage CE + niveau 1 ou 2. Sanction : <strong>68 € + 1 point</strong>. <a href='/magazine/gants-moto-homologues' class='underline'>Lire le guide gants →</a>" },
      ],
      cardsCols: 2,
    },
    {
      part: 'Partie 2 — Fortement recommandés',
      title: 'Les 4 EPI qui font la différence',
      normCards: [
        { code: 'EN 17092', color: '#1d4ed8', title: 'Blouson moto', body: "Norme blouson moto homologué (AAA, AA, A, B, C). Cuir ou textile. Coques rigides aux épaules et coudes (EN 1621-1)." },
        { code: 'EN 1621-2', color: '#16a34a', title: 'Dorsale (protection dos)', body: "Niveau 1 (loisir) ou niveau 2 (sport). Évite 90 % des lésions médullaires en cas de chute sur le dos." },
        { code: 'EN 13634', color: '#cf2031', title: 'Bottes moto', body: "Hauteur cheville renforcée + protection malléoles + semelle anti-perforation. Niveau 1 ou 2." },
      ],
    },
    {
      part: 'Partie 3 — Pantalon & airbag',
      title: 'Investissements évolués',
      cards: [
        { icon: ShieldCheck, accent: '#1d4ed8', kicker: 'EN 17092', title: 'Pantalon moto', body: "Cuir ou textile renforcé type Cordura/Kevlar. Coques aux genoux et hanches (EN 1621-1). Évite les brûlures par friction." },
        { icon: Lightbulb, accent: '#7f1d1d', kicker: 'EN 1621-4', title: 'Airbag moto', body: "Déploiement en 60 ms via cordon ou capteurs (gyroscope). Réduit de <strong>30-40 %</strong> les lésions thoraciques mortelles." },
      ],
      cardsCols: 2,
      callouts: [
        { icon: Lightbulb, accent: '#16a34a', body: "<strong>Astuce équipement :</strong> achète par ordre d’importance → 1) casque (haut de gamme), 2) gants (EN 13594 niveau 2), 3) blouson + dorsale, 4) bottes, 5) pantalon, 6) airbag. Budget réaliste : 800-1500 € pour du complet de qualité." },
      ],
    },
    {
      part: 'Partie 4 — Récapitulatif normes',
      title: 'Tableau des normes EPI 2026',
      checkLists: [
        {
          kicker: 'Normes obligatoires', accent: '#cf2031',
          items: [
            { ok: true, text: '<strong>ECE 22-06</strong> — Casques moto (depuis 2024)' },
            { ok: true, text: '<strong>EN 13594:2015</strong> — Gants moto (depuis 2016)' },
          ],
        },
        {
          kicker: 'Normes recommandées', accent: '#16a34a',
          items: [
            { ok: true, text: '<strong>EN 17092</strong> — Vêtements (blouson, pantalon)' },
            { ok: true, text: '<strong>EN 1621-1</strong> — Coques articulations (épaule, coude, genou, hanche)' },
            { ok: true, text: '<strong>EN 1621-2</strong> — Dorsale' },
            { ok: true, text: '<strong>EN 1621-4</strong> — Airbag moto' },
            { ok: true, text: '<strong>EN 13634</strong> — Bottes moto' },
          ],
        },
      ],
    },
  ],
  quiz: [
    { id: 'q1', question: 'Quels sont les 2 EPI légalement obligatoires en France ?', options: ['Casque + dorsale', 'Casque + gants', 'Casque + blouson + gants', 'Tous les EPI'], correctIndex: 1, explain: "R431-1 (casque) et R431-1-2 (gants) = les 2 seuls EPI obligatoires. Le reste est fortement recommandé." },
    { id: 'q2', question: "Quelle norme certifie une dorsale moto ?", options: ['EN 13594', 'EN 1621-1', 'EN 1621-2', 'EN 17092'], correctIndex: 2, explain: "EN 1621-2 = norme spécifique dorsale. Niveau 1 (loisir) ou 2 (sport). EN 1621-1 concerne les coques d’articulations." },
    { id: 'q3', question: 'Quelle norme couvre les blousons et pantalons moto ?', options: ['EN 13634', 'EN 17092', 'EN 1621-1', 'EN 13594'], correctIndex: 1, explain: "EN 17092 — norme harmonisée vêtements moto. Niveaux AAA, AA, A, B, C selon le niveau d’abrasion et de protection." },
    { id: 'q4', question: "Quelle est la sanction pour absence de casque ECE 22-06 ?", options: ['68 € + 1 point', '135 € + 3 points', '375 € + 6 points', "Avertissement"], correctIndex: 1, explain: "Article R431-1 : amende 4ᵉ classe 135 € + retrait 3 points. Pour conducteur ET passager." },
    { id: 'q5', question: "Le port de l’airbag moto est-il obligatoire ?", options: ['Oui depuis 2020', "Non, seulement recommandé (norme EN 1621-4)", "Oui sur autoroute", "Oui pour les 125 cm³+"], correctIndex: 1, explain: "Recommandé mais non obligatoire. Norme EN 1621-4. Réduit de 30-40 % les lésions thoraciques mortelles. Équipement le plus efficace après casque." },
  ],
  ctaFinal: {
    kicker: 'Pour aller plus loin',
    title: 'Sanctions complètes & barèmes',
    body: "Toutes les amendes, retraits de points et peines associées aux infractions du Code de la route en 2026.",
    primary: { label: 'Voir le barème complet', to: '/sanctions-baremes' },
    secondary: { label: 'Lexique EPI', to: '/lexique?slug=epi-equipement-protection' },
  },
};
